import {
  OPEN_SKILL,
  OPEN_SKILLS_PALETTE,
  Skill,
  SkillValues,
  getSkillsSnapshot,
  loadSkills,
  noteSkillRun,
  subscribeSkills
} from '@/lib/skills';
import { cn } from '@/lib/utils';
import { LayoutGrid } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';

import SkillDialog from './SkillDialog';
import SkillsPalette from './SkillsPalette';

interface Props {
  disabled?: boolean;
  onRun: (text: string, skill: Skill, values: SkillValues) => void;
}

/**
 * "All Skills": the button beside the composer, and the one place that owns
 * the palette and the form. The "/" menu and links inside answers open them
 * through window events (lib/skills.ts), so there is one palette and one form
 * however the user got there.
 *
 * Listed by the server from what the tenant's data holds - no flag - so the
 * button is absent for a tenant with none.
 */
export const SkillsButton = ({ disabled = false, onRun }: Props) => {
  const skills = useSyncExternalStore(subscribeSkills, getSkillsSnapshot);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [active, setActive] = useState<Skill | null>(null);
  const [initial, setInitial] = useState<SkillValues | undefined>();
  // The copilot widget renders this composer with no router at all; the hooks
  // then throw, as useQuery does in MessageComposer, and the link is simply
  // not read there.
  let search = '';
  let navigate: NavigateFunction | null = null;
  try {
    search = useLocation().search;
    navigate = useNavigate();
  } catch {
    /* no router: nothing to read */
  }
  const wanted = new URLSearchParams(search).get('skill');

  // "/?skill=<id>": another page (Know your data) asked for this skill's form.
  // Opened once the list has loaded, then dropped from the address so a
  // reload does not open it again. An id the user cannot run is ignored.
  useEffect(() => {
    if (!wanted || disabled || !skills.length || !navigate) return;
    const skill = skills.find((s) => s.id === wanted);
    if (skill) {
      setPaletteOpen(false);
      setInitial(undefined);
      setActive(skill);
    }
    const next = new URLSearchParams(search);
    next.delete('skill');
    const rest = next.toString();
    navigate({ search: rest ? `?${rest}` : '' }, { replace: true });
  }, [wanted, disabled, skills, search, navigate]);

  useEffect(() => {
    loadSkills();
    const openPalette = () => {
      if (!disabled) setPaletteOpen(true);
    };
    const openOne = (e: Event) => {
      const { id, values } = (e as CustomEvent).detail || {};
      const skill = getSkillsSnapshot().find((s) => s.id === id);
      if (!skill || disabled) return;
      setPaletteOpen(false);
      setInitial(values);
      setActive(skill);
    };
    window.addEventListener(OPEN_SKILLS_PALETTE, openPalette);
    window.addEventListener(OPEN_SKILL, openOne);
    return () => {
      window.removeEventListener(OPEN_SKILLS_PALETTE, openPalette);
      window.removeEventListener(OPEN_SKILL, openOne);
    };
  }, [disabled]);

  if (!skills.length) return null;

  return (
    <>
      <Button
        id="skills-open"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={() => setPaletteOpen(true)}
        title="All skills (type / in the message box)"
        className={cn(
          'flex items-center h-9 px-3 rounded-full font-medium text-[13px] gap-1.5',
          'hover:bg-muted hover:dark:bg-muted transition-all duration-200',
          paletteOpen && 'bg-muted/50'
        )}
      >
        <LayoutGrid className="!size-5" />
        <span className="hidden sm:inline">All Skills</span>
      </Button>
      <SkillsPalette
        open={paletteOpen}
        skills={skills}
        onClose={() => setPaletteOpen(false)}
        onPick={(s) => {
          setPaletteOpen(false);
          setInitial(undefined);
          setActive(s);
        }}
      />
      <SkillDialog
        skill={active}
        initial={initial}
        onClose={() => setActive(null)}
        onRun={(text, skill, values) => {
          setActive(null);
          noteSkillRun(skill.id);
          onRun(text, skill, values);
        }}
      />
    </>
  );
};

export default SkillsButton;
